import { useEffect, useState } from 'react';
import { Bell, BellOff, Check, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { api } from '@/lib/site';

const STORAGE = 'clickgrow-push';
function readLocal() { try { return JSON.parse(localStorage.getItem(STORAGE) || '{}'); } catch (_) { return {}; } }
function applicationKey(value) {
  const bytes = atob(value.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - value.length % 4) % 4));
  return Uint8Array.from(bytes, char => char.charCodeAt(0));
}

export default function NotificationOptIn() {
  const location = useLocation();
  const [config, setConfig] = useState(null);
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [opened, setOpened] = useState(false);
  const [message, setMessage] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [receiptDismissed, setReceiptDismissed] = useState(false);
  const supported = typeof window !== 'undefined' && window.isSecureContext && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const iosNeedsInstall = ios && !window.matchMedia('(display-mode: standalone)').matches && !navigator.standalone;
  const params = new URLSearchParams(location.search);
  const receiptId = params.get('push_receipt');
  const receiptSignature = params.get('push_signature');

  useEffect(() => {
    if (!window.isSecureContext) return;
    let cancelled = false;
    api.get('/push/config').then(async ({ data }) => {
      if (cancelled || !data.enabled) return;
      setConfig(data);
      if (!supported) return;
      const registration = await navigator.serviceWorker.register('/push-sw.js', { scope: '/', updateViaCache: 'none' });
      const subscription = await registration.pushManager.getSubscription();
      if (cancelled) return;
      const local = readLocal();
      setSubscribed(!!subscription && Notification.permission === 'granted' && !!local.id && !local.disabled);
      if (subscription && local.token && !local.disabled && Notification.permission === 'granted') {
        const response = await api.post('/push/subscribe', { token: local.token, subscription: subscription.toJSON(), device: navigator.userAgent.slice(0, 120) });
        localStorage.setItem(STORAGE, JSON.stringify({ ...local, id: response.data.id }));
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [supported]);

  async function enable() {
    setBusy(true); setMessage('');
    try {
      // Permission request happens directly in response to this button click.
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') { setMessage('To enable updates, allow notifications in your browser’s site settings.'); return; }
      const registration = await navigator.serviceWorker.ready;
      let local = readLocal();
      if (!local.token) local = { token: Array.from(crypto.getRandomValues(new Uint8Array(32)), byte => byte.toString(16).padStart(2, '0')).join('') };
      local.disabled = false;
      // Save the browser token before the request so a network failure can be retried safely.
      localStorage.setItem(STORAGE, JSON.stringify(local));
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: applicationKey(config.public_key) });
      const { data } = await api.post('/push/subscribe', { token: local.token, subscription: subscription.toJSON(), device: navigator.userAgent.slice(0, 120) });
      localStorage.setItem(STORAGE, JSON.stringify({ ...local, id: data.id }));
      setSubscribed(true); setMessage('Updates enabled. Your browser reference: ' + data.id.slice(0, 8));
    } catch (error) { setMessage(error?.response?.data?.detail || 'Could not enable notifications. Check browser permissions and try again.'); }
    finally { setBusy(false); }
  }

  async function disable() {
    setBusy(true);
    try {
      const local = readLocal();
      localStorage.setItem(STORAGE, JSON.stringify({ ...local, disabled: true }));
      setSubscribed(false);
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) await subscription.unsubscribe();
      if (local.id && local.token) await api.post('/push/unsubscribe', local);
      setSubscribed(false); setMessage('Notifications turned off for this browser.');
    } catch (_) { setMessage('Could not update your subscription. You can also turn notifications off in browser settings.'); }
    finally { setBusy(false); }
  }

  async function acknowledge() {
    setBusy(true);
    try {
      await api.post('/push/receipt', { id: receiptId, signature: receiptSignature, event: 'acknowledged' });
      setAcknowledged(true); setMessage('Marked as read.');
      const url = new URL(window.location.href); url.searchParams.delete('push_receipt'); url.searchParams.delete('push_signature');
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    } catch (_) { setMessage('Unable to mark this update as read.'); }
    finally { setBusy(false); }
  }

  if ((!supported && !iosNeedsInstall) || !config?.enabled) return null;
  const hasReceipt = receiptId && receiptSignature && !acknowledged && !receiptDismissed;
  const local = readLocal();
  return <div className="fixed bottom-5 left-4 z-40 max-w-[calc(100vw-6rem)] sm:max-w-sm">
    {(opened || hasReceipt) && <div className="mb-3 rounded-2xl border bg-white text-slate-900 shadow-xl p-5">
      <div className="flex justify-between items-center gap-4"><h2 className="font-semibold">Project updates & news</h2><button onClick={() => { setOpened(false); setReceiptDismissed(true); }} aria-label="Close notification settings"><X className="w-4 h-4" /></button></div>
      <p className="mt-2 text-sm text-slate-600">Allow this browser to receive updates. You can turn them off anytime.</p>
      {iosNeedsInstall && <p className="mt-3 text-sm text-slate-600">On iPhone or iPad, use Share → Add to Home Screen. Open the installed website, then allow notifications. Requires iOS/iPadOS 16.4 or newer.</p>}
      {subscribed && local.id && <p className="mt-3 text-xs text-slate-500">Browser reference: <strong>{local.id.slice(0, 8)}</strong></p>}
      {hasReceipt && <button disabled={busy} onClick={acknowledge} className="mt-3 rounded-lg bg-emerald-600 px-4 py-2 text-sm text-white">Mark update as read</button>}
      {!iosNeedsInstall && <button disabled={busy} onClick={subscribed ? disable : enable} className="mt-3 flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-50">{subscribed ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4" />}{busy ? 'Please wait…' : subscribed ? 'Turn off notifications' : 'Allow notifications'}</button>}
      {message && <p role="status" className="mt-3 text-sm text-slate-600">{message}</p>}
    </div>}
    <button onClick={() => setOpened(value => !value)} className="inline-flex items-center gap-2 rounded-full border bg-white px-4 py-3 text-sm text-slate-800 shadow-lg">{subscribed ? <Check className="w-4 h-4 text-blue-600" /> : <Bell className="w-4 h-4 text-blue-600" />}{subscribed ? 'Update settings' : 'Get updates'}</button>
  </div>;
}
